import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import clsx from 'clsx';
import { IconCalendar } from '@americanexpress/dls-icons';
import { Input, Label } from '@americanexpress/dls-react';
import { Experiment, Variant } from '@americanexpress/one-experiments';
import { DEFAULT_DATE_FORMAT } from '../../constants/dateFormatConstants';
import {
  KEYCODE_DOWN_ARROW,
  KEYCODE_UP_ARROW,
} from '../../constants/keyConstants';
import {
  useDateContext,
  useDateDispatch,
  useDateSelector,
} from '../../context/dateContext';
import { setFocusedField } from '../../ducks/dateDuck';
import { getStateKey } from '../../selectors/commonSelectors';
import {
  areLuxonDatesEqual,
  convertDefaultFormatStringDateToLocaleFormat,
  getAriaLabel,
} from '../../utils/dateUtils';
import {
  getFocusedField,
  getIsDateDisabled,
  getIsDateAvailable,
} from '../../selectors/dateSelectors';
import styles from './DateInput.scss';
import { HOMEPAGE_REDESIGN_EXPERIMENT } from '../../constants/experimentConstant';

export const useSetInputFocus = ({ focusedField, inputRef, stateKey }) => {
  useEffect(() => {
    if (inputRef?.current && focusedField === stateKey) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [focusedField, inputRef, stateKey]);
};

export const useLocalValue = ({ setLocalValue, setIsEditing, stateValue }) => {
  useEffect(() => {
    setIsEditing(false);
    setLocalValue(convertDefaultFormatStringDateToLocaleFormat(stateValue));
  }, [stateValue, setIsEditing, setLocalValue]);
};

export const useKeyboardFocus = ({
  isKeyboardEditing,
  inputRef,
  activeElement,
  localValue,
}) => {
  useEffect(() => {
    if (!isKeyboardEditing && inputRef && activeElement === inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [localValue, isKeyboardEditing, inputRef, activeElement]);
};

export const bindOnFocus = ({ dispatch, stateKey }) => () => {
  dispatch(setFocusedField(stateKey));
};

export const bindOnChange = ({ setLocalValue }) => ({ target: { value } }) => {
  setLocalValue(value);
};

export const bindOnBlur = ({
  localValue: localStringDate,
  stateValue: stateStringDate,
  state,
  dispatch,
  onChangeAction,
  setIsEditing,
  setLocalValue,
}) => () => {
  const localLuxonDate = DateTime.fromFormat(localStringDate, 'D');
  const localStringFormattedDate = localLuxonDate?.toFormat(DEFAULT_DATE_FORMAT);
  let stateLuxonDate = null;
  if (stateStringDate) {
    stateLuxonDate = DateTime.fromFormat(
      stateStringDate,
      DEFAULT_DATE_FORMAT
    );
  }
  if (
    localLuxonDate.isValid
      && !areLuxonDatesEqual(localLuxonDate, stateLuxonDate)
      && !getIsDateDisabled({ state }, localStringFormattedDate)
      && getIsDateAvailable({ state }, localStringFormattedDate)
  ) {
    dispatch(onChangeAction({ date: localStringFormattedDate }));
  } else {
    setIsEditing(false);
    setLocalValue(
      convertDefaultFormatStringDateToLocaleFormat(stateStringDate)
    );
  }
};

export const bindKeyboardPress = ({
  isKeyboardEditing,
  setIsEditing,
  localValue,
  initialDate,
  onChangeAction,
  dispatch,
}) => (event) => {
  if (!isKeyboardEditing) {
    let plusDays;

    switch (event.keyCode) {
      case KEYCODE_UP_ARROW: {
        plusDays = -1;
        break;
      }
      case KEYCODE_DOWN_ARROW: {
        plusDays = 1;
        break;
      }
      default:
        setIsEditing(true);
        return;
    }
    if (!DateTime.fromFormat(localValue, 'D').isValid) {
      dispatch(onChangeAction({ date: initialDate, plusDays }));
    } else {
      dispatch(onChangeAction({ plusDays }));
    }
  }
};

const DateInput = ({
  className,
  stateKey,
  initialDate,
  onChangeAction,
  placeholder,
}) => {
  const { state } = useDateContext();
  const inputRef = useRef();
  const dispatch = useDateDispatch();
  const focusedField = useDateSelector(getFocusedField);
  const stateValue = useDateSelector(getStateKey(stateKey));
  const [isKeyboardEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(
    convertDefaultFormatStringDateToLocaleFormat(stateValue)
  );

  useSetInputFocus({
    focusedField,
    inputRef,
    stateKey,
  });

  useLocalValue({
    setLocalValue,
    setIsEditing,
    stateValue,
  });

  useKeyboardFocus({
    isKeyboardEditing,
    inputRef,
    activeElement: document.activeElement,
    localValue,
  });

  const variant = (
    <Label htmlFor={`date-input-${stateKey}`} className="margin-1-b">{placeholder}</Label>
  );

  return (
    <div className={clsx(className, 'flex flex-wrap flex-column height-full')}>
      <Experiment
        experimentName={HOMEPAGE_REDESIGN_EXPERIMENT}
        dimensionName="search_widget"
        defaultVariant="control"
      >
        <Variant variant="control">
          <div className="flex flex-align-center">
            <IconCalendar
              title="Calendar Icon"
              titleId="calendarIcon"
              size="sm"
              color="neutral"
            />
            <label htmlFor={`date-input-${stateKey}`} className="body color-text-subtle margin-0">{placeholder}</label>
          </div>
        </Variant>
        <Variant variant="widget_a_with_hero">{variant}</Variant>
        <Variant variant="widget_a_no_hero">{variant}</Variant>
        <Variant variant="widget_b_with_hero">{variant}</Variant>
        <Variant variant="widget_b_no_hero">{variant}</Variant>
      </Experiment>
      <Input
        id={`date-input-${stateKey}`}
        onKeyDown={bindKeyboardPress({
          isKeyboardEditing,
          setIsEditing,
          localValue,
          initialDate,
          onChangeAction,
          dispatch,
        })}
        onFocus={bindOnFocus({ dispatch, stateKey })}
        onBlur={bindOnBlur({
          localValue,
          stateValue,
          state,
          dispatch,
          onChangeAction,
          setIsEditing,
          setLocalValue,
        })}
        ref={inputRef}
        value={localValue}
        onChange={bindOnChange({ setLocalValue })}
        placeholder={placeholder}
        aria-describedby="date-picker-field-description"
        aria-label={getAriaLabel(stateValue, placeholder)}
        autoComplete="off"
        className={styles.inputField}
      />
    </div>
  );
};

DateInput.propTypes = {
  className: PropTypes.string,
  stateKey: PropTypes.string.isRequired,
  initialDate: PropTypes.string.isRequired,
  onChangeAction: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
};

DateInput.defaultProps = {
  className: null,
};

export default DateInput;
